import React, { useEffect, useState } from "react";
import {
    Box,
    Stack,
    Typography,
    Paper,
    Chip,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";

const cardSx = {
    bgcolor: "#1C252E",
    borderRadius: "12px",
    border: "1px solid #2F3A45",
    p: 2,
    transition: "all .2s ease",
    "&:hover": {
        borderColor: "rgba(0, 184, 217, 0.4)",
    }
};

const innerBoxSx = {
    bgcolor: "#111A21",
    p: 2,
    borderRadius: "10px",
    border: "1px solid rgba(0, 184, 217, 0.15)",
};

const getDifficultyConfig = (diff, sev) => {
    const val = (sev || diff || "Medium").toString();
    const lower = val.toLowerCase();
    if (lower.includes("high") || lower.includes("hard") || lower.includes("severe")) {
        return { label: val, color: "#FF5630", bg: "rgba(255, 86, 48, 0.16)" };
    }
    if (lower.includes("med")) {
        return { label: val, color: "#FFAB00", bg: "rgba(255, 171, 0, 0.16)" };
    }
    if (lower.includes("easy") || lower.includes("low")) {
        return { label: val, color: "#36B37E", bg: "rgba(54, 179, 126, 0.16)" };
    }
    return { label: val, color: "#00B8D9", bg: "rgba(0, 184, 217, 0.16)" };
};

const getThumbnailUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
    }
    const base = (process.env.REACT_APP_API_PATH || "").replace(/\/api\/?$/, "").replace(/\/+$/, "");
    const path = url.startsWith("/") ? url : `/${url}`;
    return base ? `${base}${path}` : url;
};

const ProfileCard_ttx = ({ team, name, scenario, currentScore, maxScore, currentQuestion }) => {
    const [imgErr, setImgErr] = useState(false);

    const secureThumbnail = getThumbnailUrl(scenario?.thumbnail_url);

    useEffect(() => {
        setImgErr(false);
    }, [scenario?.thumbnail_url]);

    const diffConfig = getDifficultyConfig(scenario?.difficulty, scenario?.severity);

    return (
        <Stack spacing={2}>
            {/* Main Scenario Details Paper */}
            <Paper sx={cardSx}>
                {/* Thumbnail Header */}
                <Box
                    sx={{
                        width: "100%",
                        height: "160px",
                        borderRadius: "10px",
                        overflow: "hidden",
                        bgcolor: "#111A21",
                        border: "1px solid rgba(0, 184, 217, 0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        mb: 2,
                    }}
                >
                    {secureThumbnail && !imgErr ? (
                        <Box
                            component="img"
                            src={secureThumbnail}
                            alt={scenario?.name || "Scenario Thumbnail"}
                            onError={() => setImgErr(true)}
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                width: "100%",
                                height: "100%",
                                background: "linear-gradient(135deg, #111A21 0%, #162638 100%)",
                                p: 2,
                                textAlign: "center",
                            }}
                        >
                            <SecurityIcon sx={{ fontSize: 42, color: "#00B8D9", opacity: 0.8 }} />
                            <Typography
                                variant="caption"
                                sx={{ color: "#919EAB", fontWeight: 600, fontSize: "0.75rem", px: 1 }}
                            >
                                {scenario?.name || "Cyber Scenario"}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Score Tracker (If present) */}
                {maxScore > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: "8px",
                            bgcolor: "rgba(0, 184, 217, 0.08)",
                            border: "1px solid rgba(0, 184, 217, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box>
                            <Typography variant="caption" sx={{ color: "#919EAB", display: "block" }}>
                                Question Progress
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "#00B8D9", fontWeight: 700 }}>
                                #{currentQuestion || 1}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ color: "#919EAB", display: "block" }}>
                                Score
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "#36B37E", fontWeight: 700 }}>
                                {currentScore || 0} / {maxScore} pts
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Scenario Metadata Card */}
                <Box sx={innerBoxSx}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#00B8D9",
                            fontWeight: 700,
                            letterSpacing: 1.1,
                            textTransform: "uppercase",
                            fontSize: "0.68rem",
                            display: "block",
                            mb: 1.5,
                        }}
                    >
                        Scenario Information
                    </Typography>

                    <Stack spacing={1.2}>
                        {/* Scenario Name */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                pb: 1,
                                borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                            }}
                        >
                            <Typography
                                sx={{
                                    width: 80,
                                    minWidth: 80,
                                    color: "#919EAB",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                    pt: "2px",
                                }}
                            >
                                Scenario:
                            </Typography>
                            <Typography
                                sx={{
                                    flex: 1,
                                    color: "#FFFFFF",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    lineHeight: 1.45,
                                    wordBreak: "break-word",
                                }}
                            >
                                {scenario?.name || "N/A"}
                            </Typography>
                        </Box>

                        {/* Category */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                pb: 1,
                                borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                            }}
                        >
                            <Typography
                                sx={{
                                    width: 80,
                                    minWidth: 80,
                                    color: "#919EAB",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                    pt: "2px",
                                }}
                            >
                                Category:
                            </Typography>
                            <Typography
                                sx={{
                                    flex: 1,
                                    color: "#C4CDD5",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                    lineHeight: 1.4,
                                    wordBreak: "break-word",
                                }}
                            >
                                {scenario?.category_name || scenario?.category || "N/A"}
                            </Typography>
                        </Box>

                        {/* Type */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                pb: 1,
                                borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                            }}
                        >
                            <Typography
                                sx={{
                                    width: 80,
                                    minWidth: 80,
                                    color: "#919EAB",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                    pt: "2px",
                                }}
                            >
                                Type:
                            </Typography>
                            <Typography
                                sx={{
                                    flex: 1,
                                    color: "#C4CDD5",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                    lineHeight: 1.4,
                                    wordBreak: "break-word",
                                }}
                            >
                                {scenario?.type || "N/A"}
                            </Typography>
                        </Box>

                        {/* Difficulty */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    width: 80,
                                    minWidth: 80,
                                    color: "#919EAB",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                }}
                            >
                                Difficulty:
                            </Typography>
                            <Chip
                                label={diffConfig.label}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontWeight: 700,
                                    fontSize: "0.75rem",
                                    bgcolor: diffConfig.bg,
                                    color: diffConfig.color,
                                    border: `1px solid ${diffConfig.color}40`,
                                }}
                            />
                        </Box>
                    </Stack>
                </Box>
            </Paper>

            {/* Participant Info Card (User & Team) */}
            <Paper sx={cardSx}>
                <Box sx={innerBoxSx}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#00B8D9",
                            fontWeight: 700,
                            letterSpacing: 1.1,
                            textTransform: "uppercase",
                            fontSize: "0.68rem",
                            display: "block",
                            mb: 1.5,
                        }}
                    >
                        Participant Info
                    </Typography>

                    <Stack spacing={1.2}>
                        {/* User */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                pb: 1,
                                borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                            }}
                        >
                            <Typography
                                sx={{
                                    width: 80,
                                    minWidth: 80,
                                    color: "#919EAB",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                }}
                            >
                                User:
                            </Typography>
                            <Chip
                                label={name || "N/A"}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.78rem",
                                    bgcolor: "rgba(0, 184, 217, 0.12)",
                                    color: "#00B8D9",
                                    border: "1px solid rgba(0, 184, 217, 0.3)",
                                }}
                            />
                        </Box>

                        {/* Team */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    width: 80,
                                    minWidth: 80,
                                    color: "#919EAB",
                                    fontWeight: 500,
                                    fontSize: "0.8125rem",
                                }}
                            >
                                Team:
                            </Typography>
                            <Chip
                                label={team || "N/A"}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.78rem",
                                    bgcolor: "rgba(0, 184, 217, 0.12)",
                                    color: "#00B8D9",
                                    border: "1px solid rgba(0, 184, 217, 0.3)",
                                }}
                            />
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Stack>
    );
};

export default ProfileCard_ttx;